import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Trash2, Gauge, Plus, ChevronRight, CheckCircle,
  PlusCircle, Paperclip, X, Pencil, Share2, FileText,
  AlertCircle, Clock, ShieldCheck, ScanLine, History,
  Car as CarIcon, Wrench, Link as LinkIcon, Unlink, MoreHorizontal,
} from "lucide-react";
import { api } from "../api/client";
import { Button, Field, IconButton } from "../components/UIKit";
import { StatusBadge } from "../components/StatusBadge";
import { ErrorState } from "../components/EmptyState";
import { HistoryDetailSheet } from "../components/HistoryDetailSheet";
import { ShareModal } from "../components/ShareModal";
import { HealthScoreBadge, HealthDisclaimer } from "../components/HealthScoreBadge";
import DocumentViewer from "../components/DocumentViewer";
import { pickImage } from "../lib/imagePicker";
import {
  formatDate, formatKm, formatEuros, statusColors, RULES_LABELS, humanDelay, mapNetworkError,
} from "../lib/fleet";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1616363088386-31c4a8414858?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70";
const RECENT_OPS_LIMIT = 3;

// Kinds allowed in the "Dernières opérations" list. Technical/system events
// (share_created, share_revoked, driver_mode_*) are intentionally excluded
// so the list stays useful and human-readable for non-technical users.
const RECENT_OPS_ALLOWED_KINDS = new Set([
  "intervention", "document", "mileage", "vehicle_created",
]);

const USAGE_PROFILE_LABELS = {
  taxi_vtc: "VTC / Taxi",
  intensive: "Intensif",
  standard: "Standard",
  low: "Occasionnel",
};

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [modal, setModal] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/vehicles/${id}/full`);
      setFull(data);
      setErrorMsg(null);
    } catch (e) {
      setErrorMsg(mapNetworkError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const vehicle = full?.vehicle;
  const interventions = full?.interventions || [];
  const smart = full?.smart_summary || {};
  const upcoming = full?.upcoming || [];
  const timeline = full?.timeline || [];
  const shares = full?.shares || [];

  const customMaintenance = vehicle?.custom_maintenance || [];
  const allDocuments = vehicle?.documents || [];
  const maintenance = (vehicle?.rules || []).filter((r) => r.category === "maintenance");

  const documentsByType = useMemo(() => {
    const cats = [
      { type: "assurance", label: "Assurance" },
      { type: "controle_technique", label: "Contrôle technique" },
      { type: "carte_grise", label: "Carte grise" },
      { type: "macaron_vtc", label: "Macaron VTC" },
      { type: "autre_document", label: "Autres documents" },
    ];
    return cats.map((cat) => ({ ...cat, items: allDocuments.filter((d) => d.type === cat.type) }));
  }, [allDocuments]);

  const openIntervention = (type, custom) => {
    const params = new URLSearchParams();
    params.set("vehicle_id", vehicle.id);
    params.set("km", String(vehicle.mileage));
    if (type) params.set("type", type);
    if (custom) params.set("custom", "1");
    navigate(`/intervention/new?${params.toString()}`);
  };

  const openInterventionForCustom = (cm) => {
    const params = new URLSearchParams();
    params.set("vehicle_id", vehicle.id);
    params.set("km", String(vehicle.mileage));
    params.set("custom_type_id", cm.id);
    params.set("custom_title", cm.title);
    navigate(`/intervention/new?${params.toString()}`);
  };

  const confirmDeleteVehicle = async () => {
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Véhicule placé dans la corbeille", {
        description: "Vous pouvez le restaurer depuis votre profil.",
      });
      navigate(-1);
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const replacePhoto = async () => {
    const img = await pickImage({ aspect: [4, 3] });
    if (!img) return;
    setFull((prev) => (prev ? { ...prev, vehicle: { ...prev.vehicle, _photoUploading: true } } : prev));
    try {
      await api.patch(`/vehicles/${vehicle.id}`, { photo: img });
      toast.success("Photo mise à jour");
      await load();
    } catch (e) {
      setFull((prev) => (prev ? { ...prev, vehicle: { ...prev.vehicle, _photoUploading: false } } : prev));
      const status = e?.response?.status;
      toast.error(
        status === 422 || status === 413
          ? "Image trop lourde. Choisissez une photo plus légère."
          : "Mise à jour impossible. Vérifiez votre connexion et réessayez."
      );
    }
  };

  const deletePhoto = async () => {
    try {
      await api.patch(`/vehicles/${vehicle.id}`, { photo: null });
      toast.success("Photo supprimée");
      load();
    } catch { toast.error("Suppression impossible"); }
  };

  const clearDocExpiration = async (interventionId) => {
    try {
      await api.patch(`/interventions/${interventionId}`, { expiration_date: null });
      toast.success("Date d'expiration réinitialisée");
      load();
    } catch { toast.error("Réinitialisation impossible"); }
  };

  const confirmDeleteHistory = async (interventionId) => {
    try {
      await api.delete(`/interventions/${interventionId}`);
      toast.success("Entrée supprimée");
      load();
    } catch { toast.error("Suppression impossible"); }
  };

  const replaceDocFile = async (interventionId) => {
    const img = await pickImage();
    if (!img) return;
    try {
      await api.patch(`/interventions/${interventionId}/attachment`, { invoice_photo: img });
      toast.success("Document remplacé");
      load();
    } catch { toast.error("Remplacement impossible"); }
  };

  const confirmDeleteDoc = async (interventionId) => {
    try {
      await api.delete(`/interventions/${interventionId}`);
      toast.success("Document supprimé");
      load();
    } catch { toast.error("Suppression impossible"); }
  };

  const createCustomMaintenance = async (v) => {
    await api.post(`/vehicles/${vehicle.id}/custom-maintenance-types`, {
      title: v.title, interval_months: v.interval_months, interval_kms: v.interval_kms,
    });
    toast.success("Entretien créé"); load();
  };

  const editCustomMaintenance = async (cmId, v) => {
    await api.patch(`/custom-maintenance-types/${cmId}`, {
      title: v.title, interval_months: v.interval_months ?? 0, interval_kms: v.interval_kms ?? 0,
    });
    toast.success("Entretien mis à jour"); load();
  };

  const confirmDeleteCM = async (cmId) => {
    try {
      await api.delete(`/custom-maintenance-types/${cmId}`);
      toast.success("Entretien supprimé"); load();
    } catch { toast.error("Suppression impossible"); }
  };

  if (loading) return <VehicleDetailSkeleton />;

  if (!vehicle) {
    return (
      <div>
        <div style={{ padding: 12 }}>
          <IconButton testID="vehicle-detail-back" icon={<ChevronLeft size={22} />} onClick={() => navigate(-1)} ariaLabel="Retour" />
        </div>
        <ErrorState message={errorMsg || "Impossible de charger le véhicule."} onRetry={() => { setLoading(true); load(); }} testID="vehicle-detail-error" />
      </div>
    );
  }

  const photo = vehicle.photo || PLACEHOLDER;
  const paceInfo = vehicle.pace?.value && vehicle.pace?.confidence === "measured"
    ? `~${vehicle.pace.value} km/jour`
    : null;
  const usageLabel = USAGE_PROFILE_LABELS[vehicle.usage_profile] || null;
  const activeShares = shares.filter((s) => !s.revoked && (!s.expires_at || new Date(s.expires_at) > new Date()));

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", paddingBottom: 100 }} data-testid="vehicle-detail-page">
      {/* Hero photo */}
      <div
        style={{ position: "relative", width: "100%", height: 280, background: "var(--mf-bg-muted)", cursor: "pointer" }}
        onClick={() => setModal({ kind: "photoMenu" })}
        data-testid="vehicle-photo-tap"
      >
        <img src={photo} alt={vehicle.brand} style={{ width: "100%", height: "100%", objectFit: "cover" }} data-testid="vehicle-photo" />
        {vehicle._photoUploading && (
          <div style={{
            position: "absolute", inset: 0, background: "rgba(11,25,44,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, zIndex: 3,
          }} data-testid="vehicle-photo-uploading">
            <div style={{
              width: 32, height: 32, borderRadius: 16,
              border: "3px solid rgba(255,255,255,0.35)", borderTopColor: "#fff",
              animation: "mf-spin 700ms linear infinite",
            }} />
            <span style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>Mise à jour de la photo…</span>
            <style>{`@keyframes mf-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 120, background: "linear-gradient(to bottom, rgba(11,25,44,0.55), rgba(11,25,44,0))" }} />
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 60, background: "linear-gradient(to bottom, rgba(248,250,252,0), rgba(248,250,252,1))" }} />
        <div style={{ position: "absolute", top: 16, left: 16, right: 16, display: "flex", justifyContent: "space-between" }} onClick={(e) => e.stopPropagation()}>
          <button data-testid="vehicle-detail-back" onClick={() => navigate(-1)}
            style={{ width: 40, height: 40, borderRadius: 20, background: "rgba(11,25,44,0.35)", color: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ChevronLeft size={22} />
          </button>
          <button data-testid="vehicle-detail-delete" onClick={() => setModal({ kind: "confirmDeleteVehicle" })}
            style={{ width: 40, height: 40, borderRadius: 20, background: "rgba(11,25,44,0.35)", color: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Trash2 size={20} />
          </button>
        </div>
      </div>

      {/* Header premium */}
      <div className="mf-anim-in" data-testid="vehicle-header" style={{
        margin: "-40px 20px 0", padding: 20,
        background: "var(--mf-bg-card)", borderRadius: 20, boxShadow: "var(--mf-shadow)", position: "relative",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--mf-text)", letterSpacing: -0.3 }}>
              {vehicle.brand} {vehicle.model}
            </h1>
            <div style={{ marginTop: 4, fontSize: 12, fontWeight: 700, color: "var(--mf-text-secondary)", letterSpacing: 1.2 }}>
              {vehicle.plate?.toUpperCase()}{vehicle.year ? ` · ${vehicle.year}` : ""}
              {usageLabel ? ` · ${usageLabel}` : ""}
            </div>
          </div>
          <StatusBadge status={vehicle.status} testID="vehicle-detail-status" />
        </div>

        {vehicle.health && typeof vehicle.health.score === "number" && (
          <div data-testid="vehicle-detail-health" style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: 14, background: "var(--mf-bg-muted)", borderRadius: 14, marginBottom: 12,
          }}>
            <HealthScoreBadge score={vehicle.health.score} level={vehicle.health.level} size={64} testID="vehicle-detail-health-badge" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "var(--mf-text-secondary)", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                État de santé
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--mf-text)", lineHeight: 1.35 }} data-testid="vehicle-detail-health-message">
                {vehicle.health.message}
              </div>
              {paceInfo && (
                <div style={{ marginTop: 4, fontSize: 12, color: "var(--mf-text-muted)" }} data-testid="vehicle-detail-pace">
                  Rythme observé : {paceInfo}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Emergency banner: only when at least one urgent event */}
        {upcoming.some((e) => e.status === "urgent") && (
          <div data-testid="vehicle-emergency-banner" style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "12px 14px", background: "rgba(239,68,68,0.10)", borderRadius: 12,
            marginBottom: 12, border: "1px solid rgba(239,68,68,0.25)",
          }}>
            <AlertCircle size={18} color="var(--mf-urgent-solid)" />
            <div style={{ fontSize: 13, color: "var(--mf-urgent-text)", fontWeight: 700 }}>
              {upcoming.filter((e) => e.status === "urgent").length} action{upcoming.filter((e) => e.status === "urgent").length > 1 ? "s" : ""} urgente{upcoming.filter((e) => e.status === "urgent").length > 1 ? "s" : ""} à traiter
            </div>
          </div>
        )}

        <button
          data-testid="vehicle-detail-mileage-btn"
          onClick={() => navigate(`/vehicles/${vehicle.id}/mileage`)}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "var(--mf-bg-muted)", borderRadius: 12, padding: 14, marginBottom: 4,
            border: "none", cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Gauge size={20} color="var(--mf-brand)" />
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 11, color: "var(--mf-text-secondary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Kilométrage
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{formatKm(vehicle.mileage)}</div>
            </div>
          </div>
          <ChevronRight size={16} color="var(--mf-text-muted)" />
        </button>

        {vehicle.health && <HealthDisclaimer testID="vehicle-detail-disclaimer" />}
      </div>

      {/* Actions principales — 3 visibles + menu Plus */}
      <Section>
        <QuickActionBar
          actions={[
            { key: "mileage", label: "Kilométrage", icon: <Gauge size={20} />, onClick: () => navigate(`/vehicles/${vehicle.id}/mileage`), tint: "var(--mf-upcoming-solid)" },
            { key: "intervention", label: "Intervention", icon: <Wrench size={20} />, onClick: () => openIntervention(), tint: "var(--mf-ok-solid)" },
            { key: "scan", label: "Scanner", icon: <ScanLine size={20} />, onClick: () => navigate(`/scan/new?vehicle_id=${vehicle.id}`), tint: "#f59e0b" },
          ]}
          onMore={() => setMoreOpen(true)}
        />
      </Section>

      {/* À venir */}
      {upcoming.length > 0 && (
        <Section title="À venir" icon={<Clock size={13} color="var(--mf-brand)" />} count={upcoming.length}>
          <UpcomingList
            events={upcoming}
            onEvent={(evt) => {
              if (evt.kind === "rule") openIntervention(evt.type);
              else if (evt.kind === "custom") {
                const cm = customMaintenance.find((c) => c.id === evt.id);
                if (cm) openInterventionForCustom(cm);
              } else if (evt.kind === "document") {
                openIntervention(evt.type, evt.type === "autre_document");
              }
            }}
          />
        </Section>
      )}

      {/* Résumé intelligent */}
      <Section title="Résumé intelligent" icon={<ShieldCheck size={13} color="var(--mf-brand)" />}>
        <SmartSummaryCard summary={smart} sme={!!vehicle.health} />
      </Section>

      {/* Documents */}
      <Section title="Documents" icon={<FileText size={13} color="var(--mf-brand)" />}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {documentsByType.map((cat) => (
            <DocumentCategory
              key={cat.type}
              cat={cat}
              onAdd={() => openIntervention(cat.type, cat.type === "autre_document")}
              onOpen={(doc) => {
                const it = interventions.find((x) => x.id === doc.id);
                if (it) setHistoryOpen(it);
              }}
            />
          ))}
        </div>
      </Section>

      {/* Entretien (rules + custom) */}
      <Section title="Entretien" icon={<Wrench size={13} color="var(--mf-brand)" />}>
        <div style={{ background: "var(--mf-bg-card)", borderRadius: 16, boxShadow: "var(--mf-shadow)", overflow: "hidden" }}>
          {maintenance.map((r, i) => (
            <RuleRow key={r.type} rule={r} last={i === maintenance.length - 1 && customMaintenance.length === 0} onClick={() => openIntervention(r.type)} />
          ))}
          {customMaintenance.map((cm, i) => (
            <CustomRow key={cm.id} cm={cm} last={i === customMaintenance.length - 1} onClick={() => setModal({ kind: "cmMenu", payload: cm })} testID={`custom-maintenance-${cm.id}`} />
          ))}
        </div>
        <button
          onClick={() => setModal({ kind: "cmCreate" })}
          data-testid="vehicle-add-custom-maintenance"
          style={{
            width: "100%", marginTop: 10, padding: "12px 14px", borderRadius: 12,
            background: "var(--mf-bg-muted)", border: "none", cursor: "pointer",
            color: "var(--mf-brand)", fontWeight: 700, fontSize: 14,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          <PlusCircle size={18} />
          Ajouter un entretien
        </button>
      </Section>

      {/* Historique interventions */}
      {interventions.length > 0 && (
        <Section title="Interventions" icon={<Wrench size={13} color="var(--mf-ok-solid)" />} count={interventions.length}>
          <div style={{ background: "var(--mf-bg-card)", borderRadius: 16, boxShadow: "var(--mf-shadow)" }}>
            {interventions.map((it, idx) => (
              <InterventionRow
                key={it.id}
                intervention={it}
                customMaintenance={customMaintenance}
                last={idx === interventions.length - 1}
                onClick={() => setHistoryOpen(it)}
                onDelete={() => setModal({ kind: "confirmDeleteHistory", payload: { id: it.id, label: labelForIntervention(it, customMaintenance) } })}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Dernières opérations — 3 max, événements utiles uniquement */}
      {(() => {
        const recent = timeline
          .filter((e) => RECENT_OPS_ALLOWED_KINDS.has(e.kind))
          .slice(0, RECENT_OPS_LIMIT);
        if (recent.length === 0) return null;
        return (
          <Section title="Dernières opérations" icon={<History size={13} color="var(--mf-brand)" />}>
            <TimelineList
              entries={recent}
              customMaintenance={customMaintenance}
              onEntry={(entry) => {
                if (entry.kind === "intervention") {
                  const it = interventions.find((x) => x.id === entry.id);
                  if (it) setHistoryOpen(it);
                }
              }}
            />
          </Section>
        );
      })()}

      {/* Active shares */}
      {activeShares.length > 0 && (
        <Section title="Partages actifs" icon={<LinkIcon size={13} color="var(--mf-brand)" />} count={activeShares.length}>
          <div style={{ background: "var(--mf-bg-card)", borderRadius: 16, boxShadow: "var(--mf-shadow)" }}>
            {activeShares.slice(0, 5).map((s, i) => (
              <div
                key={s.id}
                style={{
                  display: "flex", alignItems: "center", gap: 12, padding: 14,
                  borderBottom: i === Math.min(5, activeShares.length) - 1 ? "none" : "1px solid var(--mf-divider)",
                }}
              >
                <span style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(14,165,233,0.15)", color: "#0ea5e9", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <Share2 size={16} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{s.recipient_label || "Partage"}</div>
                  <div style={{ fontSize: 12, color: "var(--mf-text-secondary)" }}>
                    {s.intervention_ids?.length || 0} document{(s.intervention_ids?.length || 0) > 1 ? "s" : ""}
                    {s.expires_at ? ` · expire le ${formatDate(s.expires_at)}` : " · sans expiration"}
                  </div>
                </div>
                <button
                  data-testid="vehicle-share-manage"
                  onClick={() => setShareOpen(true)}
                  style={{ padding: "6px 12px", borderRadius: 999, background: "var(--mf-bg-muted)", border: "none", color: "var(--mf-brand)", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                >
                  Gérer
                </button>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Modals — identical to previous file, just moved for readability */}
      {modal?.kind === "confirmDeleteVehicle" && (
        <ConfirmModal testID="modal-delete-vehicle" title="Placer ce véhicule dans la corbeille ?"
          message="Le véhicule sera retiré de votre flotte, mais toutes ses informations seront conservées. Vous pourrez le restaurer depuis votre profil."
          confirmLabel="Mettre dans la corbeille" onConfirm={() => { setModal(null); confirmDeleteVehicle(); }}
          onCancel={() => setModal(null)} confirmTestID="confirm-delete-vehicle" />
      )}
      {modal?.kind === "photoMenu" && (
        <MenuModal title="Photo du véhicule" onClose={() => setModal(null)} items={[
          ...(vehicle.photo ? [{ label: "Voir la photo", testID: "photo-view", onPress: () => { setLightbox(vehicle.photo); setModal(null); } }] : []),
          { label: vehicle.photo ? "Remplacer la photo" : "Ajouter une photo", testID: "photo-replace", onPress: () => { setModal(null); replacePhoto(); } },
          ...(vehicle.photo ? [{ label: "Supprimer la photo", testID: "photo-delete", destructive: true, onPress: () => { setModal({ kind: "confirmDeletePhoto" }); } }] : []),
        ]} />
      )}
      {modal?.kind === "confirmDeletePhoto" && (
        <ConfirmModal title="Supprimer la photo ?" confirmLabel="Supprimer"
          onConfirm={() => { setModal(null); deletePhoto(); }} onCancel={() => setModal(null)} confirmTestID="photo-confirm-delete" />
      )}
      {modal?.kind === "confirmClearExpiration" && (
        <ConfirmModal testID="modal-clear-expiration" title="Réinitialiser la date d'expiration ?"
          message="La date d'expiration sera effacée. Aucune alerte ne sera générée tant qu'aucune nouvelle date ne sera saisie."
          confirmLabel="Réinitialiser" onConfirm={() => { const iid = modal.payload.id; setModal(null); clearDocExpiration(iid); }}
          onCancel={() => setModal(null)} confirmTestID="confirm-clear-expiration" />
      )}
      {modal?.kind === "confirmDeleteHistory" && (
        <ConfirmModal testID="modal-delete-history" title="Supprimer cette entrée ?"
          message={`« ${modal.payload.label} » sera retiré de l'historique. Cette action est irréversible mais n'affecte ni le véhicule, ni les autres entrées.`}
          confirmLabel="Supprimer" onConfirm={() => { const iid = modal.payload.id; setModal(null); confirmDeleteHistory(iid); }}
          onCancel={() => setModal(null)} confirmTestID="confirm-delete-history" />
      )}
      {modal?.kind === "confirmDeleteDoc" && (
        <ConfirmModal title={`Supprimer ${modal.payload.label} ?`} message="Cette action est irréversible."
          confirmLabel="Supprimer" onConfirm={() => { const iid = modal.payload.id; setModal(null); confirmDeleteDoc(iid); }}
          onCancel={() => setModal(null)} confirmTestID="doc-confirm-delete" />
      )}
      {modal?.kind === "cmCreate" && (
        <CustomMaintenanceModal mode="create" onClose={() => setModal(null)}
          onSubmit={async (v) => { await createCustomMaintenance(v); setModal(null); }} />
      )}
      {modal?.kind === "cmEdit" && (
        <CustomMaintenanceModal mode="edit" initial={modal.payload} onClose={() => setModal(null)}
          onSubmit={async (v) => { await editCustomMaintenance(modal.payload.id, v); setModal(null); }} />
      )}
      {modal?.kind === "cmMenu" && (
        <MenuModal title={modal.payload.title}
          message={modal.payload.history_count ? `${modal.payload.history_count} intervention${modal.payload.history_count > 1 ? "s" : ""}` : "Aucune intervention"}
          onClose={() => setModal(null)} items={[
            { label: "Ajouter une intervention", testID: "cm-menu-add", onPress: () => { openInterventionForCustom(modal.payload); setModal(null); } },
            { label: "Modifier le nom & rappels", testID: "cm-menu-edit", onPress: () => setModal({ kind: "cmEdit", payload: modal.payload }) },
            { label: "Supprimer", testID: "cm-menu-delete", destructive: true, onPress: () => setModal({ kind: "cmConfirmDelete", payload: modal.payload }) },
          ]} />
      )}
      {modal?.kind === "cmConfirmDelete" && (
        <ConfirmModal title={`Supprimer « ${modal.payload.title} » ?`}
          message={modal.payload.history_count ? `Les ${modal.payload.history_count} intervention(s) liée(s) seront également supprimées.` : "Cette action est irréversible."}
          confirmLabel="Supprimer" onConfirm={() => { const cmId = modal.payload.id; setModal(null); confirmDeleteCM(cmId); }}
          onCancel={() => setModal(null)} confirmTestID="cm-confirm-delete" />
      )}

      {moreOpen && (
        <MenuModal title="Autres actions"
          onClose={() => setMoreOpen(false)} items={[
            { label: "Modifier le véhicule", testID: "more-edit-vehicle", onPress: () => { setMoreOpen(false); navigate(`/vehicles/${vehicle.id}/edit`); } },
            { label: "Ajouter un document", testID: "more-add-document", onPress: () => { setMoreOpen(false); openIntervention("autre_document", true); } },
            { label: "Partager le véhicule", testID: "more-share-vehicle", onPress: () => { setMoreOpen(false); setShareOpen(true); } },
          ]} />
      )}

      <DocumentViewer
        open={!!lightbox}
        src={lightbox}
        mimeType="image/*"
        title="Photo du véhicule"
        onClose={() => setLightbox(null)}
      />

      {historyOpen && (
        <HistoryDetailSheet
          intervention={historyOpen} vehicle={vehicle}
          customLabel={historyOpen.type === "custom_maintenance"
            ? customMaintenance.find((c) => c.id === historyOpen.custom_type_id)?.title
            : undefined}
          onClose={() => setHistoryOpen(null)}
          onChange={async () => {
            await load();
            const fresh = (full?.interventions || []).find((x) => x.id === historyOpen.id);
            if (fresh) setHistoryOpen(fresh);
            else setHistoryOpen(null);
          }}
          onOpenLightbox={(src) => setLightbox(src)}
        />
      )}

      {shareOpen && <ShareModal vehicle={vehicle} onClose={() => { setShareOpen(false); load(); }} />}
    </div>
  );
}

/* ---------------- Skeleton ---------------- */
function VehicleDetailSkeleton() {
  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", paddingBottom: 100 }}>
      <div style={{ height: 280, background: "var(--mf-bg-muted)" }} />
      <div style={{ margin: "-40px 20px 0", padding: 20, background: "var(--mf-bg-card)", borderRadius: 20, boxShadow: "var(--mf-shadow)" }}>
        <div style={{ height: 22, width: "60%", background: "var(--mf-bg-muted)", borderRadius: 8, marginBottom: 12 }} />
        <div style={{ height: 90, background: "var(--mf-bg-muted)", borderRadius: 14, marginBottom: 12 }} />
        <div style={{ height: 60, background: "var(--mf-bg-muted)", borderRadius: 12 }} />
      </div>
      <div style={{ margin: "24px 20px", height: 60, background: "var(--mf-bg-muted)", borderRadius: 16 }} />
      <div style={{ margin: "24px 20px", height: 140, background: "var(--mf-bg-muted)", borderRadius: 16 }} />
    </div>
  );
}

/* ---------------- Section wrapper ---------------- */
function Section({ title, icon, count, children }) {
  return (
    <div style={{ marginTop: 24, padding: "0 20px" }}>
      {(title || icon) && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          {icon}
          <h3 style={{
            margin: 0, fontSize: 12, fontWeight: 800,
            color: "var(--mf-text-secondary)", textTransform: "uppercase", letterSpacing: 0.6,
          }}>{title}</h3>
          {count != null && (
            <span style={{
              padding: "1px 8px", borderRadius: 999, background: "var(--mf-bg-muted)",
              color: "var(--mf-text-secondary)", fontSize: 10, fontWeight: 800,
            }}>{count}</span>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

/* ---------------- Quick actions bar (3 visible + Plus) ---------------- */
function QuickActionBar({ actions, onMore }) {
  return (
    <div
      data-testid="vehicle-quick-actions"
      style={{
        display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 10,
        position: "relative", zIndex: 45,
      }}
    >
      {actions.slice(0, 3).map((a) => (
        <button
          key={a.key}
          onClick={a.onClick}
          data-testid={`vehicle-action-${a.key}`}
          aria-label={a.label}
          style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
            padding: "12px 6px", background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
            borderRadius: 14, color: "var(--mf-text)", cursor: "pointer",
            boxShadow: "var(--mf-shadow)", transition: "transform 150ms ease",
            minHeight: 74,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
        >
          <span style={{
            width: 34, height: 34, borderRadius: 10,
            background: `${a.tint}18`, color: a.tint,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>{a.icon}</span>
          <span style={{ fontSize: 12, fontWeight: 700 }}>{a.label}</span>
        </button>
      ))}
      <button
        onClick={onMore}
        data-testid="vehicle-action-more"
        aria-label="Plus d'actions"
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
          padding: "12px 6px", background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
          borderRadius: 14, color: "var(--mf-text)", cursor: "pointer",
          boxShadow: "var(--mf-shadow)", transition: "transform 150ms ease",
          minHeight: 74,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
      >
        <span style={{
          width: 34, height: 34, borderRadius: 10,
          background: "var(--mf-bg-muted)", color: "var(--mf-text-secondary)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <MoreHorizontal size={20} />
        </span>
        <span style={{ fontSize: 12, fontWeight: 700 }}>Plus</span>
      </button>
    </div>
  );
}

/* ---------------- Upcoming list ---------------- */
function UpcomingList({ events, onEvent }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="upcoming-list">
      {events.map((evt, i) => {
        const c = statusColors(evt.status || "ok");
        const label = evt.label || RULES_LABELS[evt.type] || evt.type;
        const isDoc = evt.kind === "document";
        const dateText = evt.next_date ? (isDoc ? `Expire le ${formatDate(evt.next_date)}` : `Prévu le ${formatDate(evt.next_date)}`) : null;
        const kmText = evt.next_mileage ? formatKm(evt.next_mileage) : null;
        const delay = humanDelay(evt.next_date);
        return (
          <button
            key={`${evt.kind}-${evt.type}-${i}`}
            onClick={() => onEvent(evt)}
            data-testid={`upcoming-row-${evt.kind}-${evt.type}`}
            style={{
              width: "100%", background: "var(--mf-bg-card)", border: "none",
              borderRadius: 14, padding: 14, boxShadow: "var(--mf-shadow)",
              display: "flex", alignItems: "center", gap: 12, textAlign: "left", cursor: "pointer",
              position: "relative", overflow: "hidden",
            }}
          >
            <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: c.solid }} />
            <div style={{ flex: 1, minWidth: 0, marginLeft: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--mf-text)" }}>{label}</span>
                {delay && evt.status !== "ok" && (
                  <span style={{ padding: "2px 8px", borderRadius: 999, background: c.bg, color: c.text, fontSize: 10, fontWeight: 800 }}>{delay}</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "var(--mf-text-secondary)" }}>
                {[dateText, kmText].filter(Boolean).join(" · ")}
              </div>
              {typeof evt.estimated_days_until === "number" && evt.estimated_days_until > 0 && evt.status !== "urgent" && (
                <div style={{ fontSize: 11, color: "var(--mf-accent)", marginTop: 2, fontWeight: 600 }}>
                  ~ dans {evt.estimated_days_until} j (estimation)
                </div>
              )}
            </div>
            <ChevronRight size={16} color="var(--mf-text-muted)" />
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Smart summary card ---------------- */
function SmartSummaryCard({ summary, sme }) {
  const { last_vidange, next_vidange, controle_technique, assurance, active_documents_count } = summary || {};
  const rows = [];

  if (last_vidange) {
    const parts = [`Il y a ${last_vidange.km_since ? formatKm(last_vidange.km_since) : "?"}`];
    if (last_vidange.date) parts.push(formatDate(last_vidange.date));
    rows.push({ key: "last-vidange", label: "Dernière vidange", value: parts.join(" · "), tint: "var(--mf-brand)" });
  }
  if (next_vidange) {
    const parts = [];
    if (next_vidange.km_to_go != null) {
      if (next_vidange.km_to_go > 0) parts.push(`Dans ${formatKm(next_vidange.km_to_go)}`);
      else if (next_vidange.km_to_go < 0) parts.push(`En retard de ${formatKm(Math.abs(next_vidange.km_to_go))}`);
      else parts.push("À faire");
    }
    if (next_vidange.date) parts.push(formatDate(next_vidange.date));
    rows.push({ key: "next-vidange", label: "Prochaine vidange", value: parts.join(" · "), status: next_vidange.status });
  }
  if (controle_technique) {
    const delay = humanDelay(controle_technique.date);
    rows.push({ key: "ct", label: "Contrôle technique", value: [formatDate(controle_technique.date), delay ? `(${delay})` : ""].filter(Boolean).join(" "), status: controle_technique.status });
  }
  if (assurance) {
    const delay = humanDelay(assurance.date);
    rows.push({ key: "insurance", label: "Assurance", value: [formatDate(assurance.date), delay ? `(${delay})` : ""].filter(Boolean).join(" "), status: assurance.status });
  }
  if (typeof active_documents_count === "number") {
    rows.push({ key: "docs", label: "Documents actifs", value: `${active_documents_count} actif${active_documents_count > 1 ? "s" : ""}`, tint: "#8b5cf6" });
  }

  if (rows.length === 0) {
    return (
      <div style={{ padding: 16, borderRadius: 16, background: "var(--mf-bg-card)", boxShadow: "var(--mf-shadow)", fontSize: 13, color: "var(--mf-text-muted)", fontStyle: "italic" }}>
        {sme ? "Ajoutez vos premiers entretiens pour voir un résumé intelligent." : "Résumé indisponible."}
      </div>
    );
  }

  return (
    <div data-testid="smart-summary" style={{ background: "var(--mf-bg-card)", borderRadius: 16, boxShadow: "var(--mf-shadow)", overflow: "hidden" }}>
      {rows.map((r, i) => {
        const c = r.status ? statusColors(r.status) : null;
        const tint = c ? c.solid : (r.tint || "var(--mf-text-secondary)");
        return (
          <div key={r.key} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            padding: "12px 16px", borderBottom: i === rows.length - 1 ? "none" : "1px solid var(--mf-divider)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: tint, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "var(--mf-text-secondary)", fontWeight: 600 }}>{r.label}</span>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--mf-text)", textAlign: "right", maxWidth: "60%" }}>
              {r.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Timeline ---------------- */
const TIMELINE_META = {
  vehicle_created: { icon: CarIcon, tint: "var(--mf-brand)", label: "Véhicule ajouté" },
  intervention: { icon: Wrench, tint: "var(--mf-ok-solid)", label: "Intervention" },
  document: { icon: FileText, tint: "#8b5cf6", label: "Document" },
  mileage: { icon: Gauge, tint: "var(--mf-upcoming-solid)", label: "Kilométrage" },
  share_created: { icon: Share2, tint: "#0ea5e9", label: "Partage créé" },
  share_revoked: { icon: Unlink, tint: "var(--mf-urgent-solid)", label: "Partage révoqué" },
};

function TimelineList({ entries, customMaintenance, onEntry }) {
  return (
    <div style={{ background: "var(--mf-bg-card)", borderRadius: 16, boxShadow: "var(--mf-shadow)", overflow: "hidden" }} data-testid="timeline-list">
      {entries.map((e, i) => {
        const isIntervention = e.kind === "intervention";
        const isDoc = isIntervention && (e.type === "autre_document" || ["assurance", "controle_technique", "carte_grise", "macaron_vtc"].includes(e.type));
        const metaKey = isIntervention ? (isDoc ? "document" : "intervention") : e.kind;
        const meta = TIMELINE_META[metaKey] || TIMELINE_META.intervention;
        const Icon = meta.icon;
        let title = meta.label;
        let subtitle = e.date ? formatDate(e.date) : "";
        let extraRight = null;
        if (isIntervention) {
          title = labelForIntervention(e, customMaintenance);
          const bits = [];
          if (e.date) bits.push(formatDate(e.date));
          if (e.mileage) bits.push(formatKm(e.mileage));
          if (e.garage) bits.push(e.garage);
          subtitle = bits.join(" · ");
          if (e.amount) extraRight = <span style={{ fontSize: 13, fontWeight: 700, color: "var(--mf-brand)" }}>{formatEuros(e.amount)}</span>;
        } else if (e.kind === "mileage") {
          title = "Kilométrage mis à jour";
          subtitle = e.mileage != null ? `${formatKm(e.mileage)} · ${formatDate(e.date)}` : formatDate(e.date);
        } else if (e.kind === "share_created" || e.kind === "share_revoked") {
          title = `${meta.label}${e.title ? ` — ${e.title}` : ""}`;
          const docCount = e.meta?.document_count;
          subtitle = [formatDate(e.date), docCount ? `${docCount} doc${docCount > 1 ? "s" : ""}` : ""].filter(Boolean).join(" · ");
        } else if (e.kind === "vehicle_created") {
          title = "Véhicule créé";
          subtitle = formatDate(e.date);
        }
        return (
          <button
            key={`${e.kind}-${e.id || e.timestamp || i}`}
            onClick={() => isIntervention && onEntry(e)}
            data-testid={`timeline-row-${i}`}
            style={{
              width: "100%", background: "transparent", border: "none",
              display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
              cursor: isIntervention ? "pointer" : "default", textAlign: "left",
              borderBottom: i === entries.length - 1 ? "none" : "1px solid var(--mf-divider)",
            }}
          >
            <span style={{
              width: 36, height: 36, borderRadius: 12,
              background: `${meta.tint}18`, color: meta.tint,
              display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Icon size={16} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--mf-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
              <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", marginTop: 2 }}>{subtitle}</div>
            </div>
            {extraRight}
            {isIntervention && <ChevronRight size={16} color="var(--mf-text-muted)" />}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Documents category ---------------- */
function DocumentCategory({ cat, onAdd, onOpen }) {
  return (
    <div data-testid={`doc-category-${cat.type}`}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, padding: "0 4px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--mf-text-secondary)", textTransform: "uppercase", letterSpacing: 0.5 }}>
          {cat.label}
          {cat.items.length > 0 && (
            <span style={{ marginLeft: 8, padding: "1px 8px", borderRadius: 999, background: "var(--mf-bg-muted)", color: "var(--mf-text-secondary)", fontSize: 10, fontWeight: 800 }}>
              {cat.items.length}
            </span>
          )}
        </div>
        <button
          onClick={onAdd}
          data-testid={`doc-add-${cat.type}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "4px 10px", borderRadius: 999, background: "transparent",
            border: "1px solid var(--mf-border)", color: "var(--mf-brand)",
            fontSize: 12, fontWeight: 700, cursor: "pointer",
          }}
        >
          <Plus size={12} /> Ajouter
        </button>
      </div>
      {cat.items.length === 0 ? (
        <div style={{ padding: "12px 16px", borderRadius: 12, background: "var(--mf-bg-card)", boxShadow: "var(--mf-shadow)", fontSize: 13, color: "var(--mf-text-muted)", fontStyle: "italic" }}>
          Aucun document
        </div>
      ) : (
        <div style={{ background: "var(--mf-bg-card)", borderRadius: 16, boxShadow: "var(--mf-shadow)", overflow: "hidden" }}>
          {cat.items.map((doc, i) => (
            <DocRow key={doc.id} doc={doc} last={i === cat.items.length - 1} onClick={() => onOpen(doc)} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Rows (reused from previous VehicleDetail) ---------------- */
function labelForIntervention(it, customMaintenance) {
  if (it.type === "custom_maintenance") return customMaintenance.find((c) => c.id === it.custom_type_id)?.title || "Entretien";
  if (it.type === "autre_document") return it.title || "Document";
  return RULES_LABELS[it.type] || it.type;
}

function InterventionRow({ intervention: it, customMaintenance, last, onClick, onDelete }) {
  const label = labelForIntervention(it, customMaintenance);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: 14,
      borderBottom: last ? "none" : "1px solid var(--mf-divider)",
    }}>
      <button
        onClick={onClick}
        data-testid={`history-row-${it.id}`}
        style={{
          flex: 1, display: "flex", alignItems: "center", gap: 12,
          background: "transparent", border: "none", cursor: "pointer", textAlign: "left", padding: 0,
        }}
      >
        <span style={{ width: 24, height: 24, borderRadius: 12, background: "var(--mf-ok-solid)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <CheckCircle size={12} color="#fff" strokeWidth={3} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{label}</div>
          <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", marginTop: 2 }}>
            {formatDate(it.date)}{it.mileage ? ` · ${formatKm(it.mileage)}` : ""}
          </div>
          {it.garage && <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 1 }}>{it.garage}</div>}
        </div>
        {it.amount && <div style={{ fontSize: 14, fontWeight: 700, color: "var(--mf-brand)" }}>{formatEuros(it.amount)}</div>}
      </button>
      <button
        onClick={onDelete}
        data-testid={`history-delete-${it.id}`}
        aria-label="Supprimer cette entrée"
        style={{
          width: 36, height: 36, borderRadius: 18, background: "rgba(239,68,68,0.08)",
          color: "var(--mf-urgent-solid)", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

function RuleRow({ rule, last, onClick }) {
  const hasData = !!rule.last_date;
  const c = statusColors(rule.status);
  const dotColor = hasData ? c.solid : "var(--mf-border)";
  const delay = humanDelay(rule.next_date);
  const isDocument = rule.category === "document";
  return (
    <button
      onClick={onClick} data-testid={`rule-${rule.type}`}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12,
        padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer",
        borderBottom: last ? "none" : "1px solid var(--mf-divider)", textAlign: "left",
      }}
    >
      <span style={{ width: 10, height: 10, borderRadius: 5, background: dotColor, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: hasData ? 700 : 500, color: hasData ? "var(--mf-text)" : "var(--mf-text-secondary)" }}>{rule.label}</span>
          {hasData && delay && rule.status !== "ok" && <span style={{ padding: "2px 8px", borderRadius: 999, background: c.bg, color: c.text, fontSize: 10, fontWeight: 800 }}>{delay}</span>}
        </div>
        {hasData ? (
          <>
            <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", marginTop: 2 }}>
              {isDocument ? "Émis le" : "Dernier ·"} {formatDate(rule.last_date)}
            </div>
            {(rule.next_date || rule.next_mileage) ? (
              <>
                <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 2 }}>
                  {isDocument ? "Expire le " : "Prochain "}{rule.next_date ? formatDate(rule.next_date) : ""}
                  {rule.next_date && rule.next_mileage ? " · " : ""}
                  {rule.next_mileage ? formatKm(rule.next_mileage) : ""}
                </div>
                {typeof rule.estimated_days_until === "number" && rule.estimated_days_until > 0 && (
                  <div style={{ fontSize: 11, color: "var(--mf-accent)", marginTop: 2, fontWeight: 600 }} data-testid={`rule-${rule.type}-estimation`}>
                    ~ dans {rule.estimated_days_until} j (estimation)
                  </div>
                )}
              </>
            ) : isDocument ? (
              <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 2, fontStyle: "italic" }}>{"Aucune date d'expiration"}</div>
            ) : null}
          </>
        ) : (
          <div style={{ fontSize: 12, color: "var(--mf-brand)", marginTop: 2, fontWeight: 500 }}>Toucher pour ajouter</div>
        )}
      </div>
      {hasData ? <ChevronRight size={16} color="var(--mf-text-muted)" /> : <Plus size={20} color="var(--mf-brand)" />}
    </button>
  );
}

function CustomRow({ cm, last, onClick, testID }) {
  const hasData = !!cm.last_date;
  const c = statusColors(cm.status);
  const dotColor = hasData ? c.solid : "var(--mf-border)";
  const delay = humanDelay(cm.next_date);
  return (
    <button
      onClick={onClick} data-testid={testID}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12,
        padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer",
        borderBottom: last ? "none" : "1px solid var(--mf-divider)", textAlign: "left",
      }}
    >
      <span style={{ width: 10, height: 10, borderRadius: 5, background: dotColor, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: hasData ? 700 : 500, color: hasData ? "var(--mf-text)" : "var(--mf-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cm.title}</span>
          {hasData && delay && cm.status !== "ok" && <span style={{ padding: "2px 8px", borderRadius: 999, background: c.bg, color: c.text, fontSize: 10, fontWeight: 800 }}>{delay}</span>}
        </div>
        {hasData ? (
          <>
            <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", marginTop: 2 }}>Dernier · {formatDate(cm.last_date)}</div>
            {(cm.next_date || cm.next_mileage) && (
              <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 2 }}>
                Prochain {cm.next_date ? formatDate(cm.next_date) : ""}
                {cm.next_date && cm.next_mileage ? " · " : ""}
                {cm.next_mileage ? formatKm(cm.next_mileage) : ""}
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", marginTop: 2 }}>Aucune intervention</div>
        )}
      </div>
      <ChevronRight size={16} color="var(--mf-text-muted)" />
    </button>
  );
}

function DocRow({ doc, last, onClick }) {
  const c = statusColors(doc.status);
  const delay = humanDelay(doc.expiration_date);
  return (
    <button
      onClick={onClick} data-testid={`doc-row-${doc.id}`}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 12,
        padding: "14px 16px", background: "transparent", border: "none", cursor: "pointer",
        borderBottom: last ? "none" : "1px solid var(--mf-divider)", textAlign: "left",
      }}
    >
      <span style={{ width: 10, height: 10, borderRadius: 5, background: c.solid, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "var(--mf-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.title}</span>
          {delay && doc.status !== "ok" && <span style={{ padding: "2px 8px", borderRadius: 999, background: c.bg, color: c.text, fontSize: 10, fontWeight: 800 }}>{delay}</span>}
        </div>
        {doc.expiration_date ? (
          <div style={{ fontSize: 12, color: "var(--mf-text-muted)", marginTop: 2 }}>Expire le {formatDate(doc.expiration_date)}</div>
        ) : (
          <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", marginTop: 2 }}>
            {doc.date ? `Émis le ${formatDate(doc.date)}` : ""}
            <span style={{ color: "var(--mf-text-muted)", fontStyle: "italic" }}>{" · Aucune date d'expiration"}</span>
          </div>
        )}
      </div>
      {doc.has_file && <Paperclip size={14} color="var(--mf-text-muted)" />}
      <ChevronRight size={16} color="var(--mf-text-muted)" />
    </button>
  );
}

/* ---------------- Modals (same as previous file) ---------------- */
function ConfirmModal({ title, message, confirmLabel, onConfirm, onCancel, confirmTestID, testID }) {
  return (
    <div onClick={onCancel} data-testid={testID} style={{
      position: "fixed", inset: 0, background: "rgba(11,25,44,0.35)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 150,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--mf-bg-card)", borderRadius: 20, padding: 24, width: "100%", maxWidth: 400,
        boxShadow: "var(--mf-shadow-strong)",
      }}>
        <h3 style={{ margin: 0, marginBottom: message ? 6 : 16, fontSize: 18, fontWeight: 800 }}>{title}</h3>
        {message && <p style={{ margin: "0 0 16px", color: "var(--mf-text-secondary)", fontSize: 14, lineHeight: 1.5 }}>{message}</p>}
        <div style={{ display: "flex", gap: 10 }}>
          <Button variant="secondary" fullWidth onClick={onCancel}>Annuler</Button>
          <Button variant="danger" fullWidth onClick={onConfirm} testID={confirmTestID}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
}

function MenuModal({ title, message, items, onClose }) {
  return (
    <div onClick={onClose} data-testid="menu-modal" style={{
      position: "fixed", inset: 0, background: "rgba(11,25,44,0.35)",
      display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 20, zIndex: 150,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--mf-bg-card)", borderRadius: 20, padding: 20, width: "100%", maxWidth: 440,
        boxShadow: "var(--mf-shadow-strong)", marginBottom: "env(safe-area-inset-bottom)",
      }} className="mf-anim-in">
        <h3 style={{ margin: 0, marginBottom: message ? 6 : 14, fontSize: 16, fontWeight: 800, textAlign: "center" }}>{title}</h3>
        {message && <p style={{ margin: "0 0 14px", color: "var(--mf-text-secondary)", fontSize: 13, textAlign: "center" }}>{message}</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((item, i) => (
            <button key={i} data-testid={item.testID} onClick={() => { item.onPress(); }}
              style={{
                padding: "14px 16px", borderRadius: 12,
                background: item.destructive ? "rgba(239,68,68,0.08)" : "var(--mf-bg-muted)",
                color: item.destructive ? "var(--mf-urgent-solid)" : "var(--mf-text)",
                fontWeight: 600, fontSize: 15, border: "none", cursor: "pointer",
              }}>
              {item.label}
            </button>
          ))}
          <button onClick={onClose} style={{
            padding: "14px 16px", borderRadius: 12, background: "transparent",
            color: "var(--mf-text-secondary)", fontWeight: 600, fontSize: 15, border: "none", cursor: "pointer",
          }}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

function CustomMaintenanceModal({ mode, initial, onClose, onSubmit }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [months, setMonths] = useState(initial?.interval_months ? String(initial.interval_months) : "");
  const [kms, setKms] = useState(initial?.interval_kms ? String(initial.interval_kms) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!title.trim()) return setError("Le titre est requis");
    setBusy(true);
    try {
      await onSubmit({
        title: title.trim(),
        interval_months: months ? parseInt(months) : null,
        interval_kms: kms ? parseInt(kms) : null,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Enregistrement impossible");
    } finally { setBusy(false); }
  };

  return (
    <div onClick={() => !busy && onClose()} data-testid="cm-modal" style={{
      position: "fixed", inset: 0, background: "rgba(11,25,44,0.35)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 150,
    }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{
        background: "var(--mf-bg-card)", borderRadius: 20, padding: 24, width: "100%", maxWidth: 460,
        boxShadow: "var(--mf-shadow-strong)",
      }}>
        <h3 style={{ margin: 0, marginBottom: 6, fontSize: 20, fontWeight: 800 }}>
          {mode === "create" ? "Nouvel entretien" : "Modifier l'entretien"}
        </h3>
        <p style={{ margin: "0 0 16px", color: "var(--mf-text-secondary)", fontSize: 14 }}>
          Définissez la périodicité (mois et/ou km).
        </p>
        <Field testID="cm-title" label="Titre" value={title} onChange={setTitle} placeholder="Ex : Lavage complet" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field testID="cm-months" label="Tous les X mois" value={months} onChange={setMonths} placeholder="12" />
          <Field testID="cm-kms" label="Ou tous les X km" value={kms} onChange={setKms} placeholder="10000" />
        </div>
        {error && <div style={{ color: "var(--mf-urgent-text)", fontSize: 13, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
          <Button variant="secondary" fullWidth onClick={onClose} disabled={busy}>Annuler</Button>
          <Button type="submit" fullWidth loading={busy} testID="cm-submit">Enregistrer</Button>
        </div>
      </form>
    </div>
  );
}
